/**
 * Google Ads conversion tracking (AW-413089823).
 *
 * The site runs no cookie banner. Tags load only for visitors outside
 * consent-required regions (EEA, UK, Switzerland, Quebec) who have not opted
 * out at /choices, so nothing is sent for anyone who opted out or is covered
 * by opt-in consent law.
 *
 * Conversion labels come straight from the Google Ads account:
 *   Growth system fit check -> AW-413089823/n-WUCOro7YYdEJ-A_cQB
 *   Strategy call booked    -> AW-413089823/_nX8CO3o7YYdEJ-A_cQB
 */

import { hasMarketingConsent } from "@/lib/consent";

export const GOOGLE_ADS_ID = "AW-413089823";

export const ADS_CONVERSIONS = {
  qualificationFormSubmit: `${GOOGLE_ADS_ID}/n-WUCOro7YYdEJ-A_cQB`,
  strategyCallBooked: `${GOOGLE_ADS_ID}/_nX8CO3o7YYdEJ-A_cQB`,
} as const;

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: GtagFn;
  }
}

function ensureGtag(): GtagFn {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      (window.dataLayer as unknown[]).push(args);
    };
  }
  return window.gtag;
}

/** Push the visitor's advertising choice to Google. */
export function updateAdsConsent(decision: "granted" | "denied") {
  if (typeof window === "undefined") return;
  const gtag = ensureGtag();
  if (decision === "denied") gtag("set", "user_data", null);
  gtag("consent", "update", {
    ad_storage: decision,
    ad_user_data: decision,
    ad_personalization: decision,
  });
}

/** Load the Google Ads tag, but only for visitors who allowed marketing cookies. */
export function loadGoogleAds() {
  if (typeof window === "undefined") return;
  if (!hasMarketingConsent()) return;
  const gtag = ensureGtag();
  updateAdsConsent("granted");
  if (document.querySelector("script[data-google-ads]")) return;
  gtag("js", new Date());
  gtag("config", GOOGLE_ADS_ID);
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  script.async = true;
  script.dataset.googleAds = "";
  document.head.appendChild(script);
}

/** Report one conversion. Silently does nothing without marketing consent. */
export function reportAdsConversion(
  sendTo: string,
  params: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") return;
  if (!hasMarketingConsent()) return;
  loadGoogleAds();
  window.gtag?.("event", "conversion", { send_to: sendTo, ...params });
}
